import { Link } from 'react-router';
import { ApiError } from '../../api/client';
import { LoadingState } from '../../components/states';
import type { MailboxesState } from './api';

/** API answers whose own message is meant for people (fixed texts; never Mail Server output). */
const SHOWN_CODES = new Set([
  'mailbox_quota_below_minimum',
  'mailbox_limit_exceeded',
  'conflict',
  'not_found',
  'mail_server_rejected',
  'mail_server_unavailable',
  'mail_server_error',
  'mail_server_not_connected',
  'network_error',
  'rate_limited',
  'unprocessable',
]);

export function mailboxErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (SHOWN_CODES.has(error.code)) return error.message;
    if (error.status === 400) return 'Check the address, size and password.';
    if (error.status === 403) return 'You are not allowed to make this change.';
  }
  return 'The change could not be made.';
}

/** Shown instead of a form while the Mail Server is not answering: no form, so no false success. */
export function MailServerNotReady({ state }: { state: MailboxesState }) {
  if (state.kind === 'loading')
    return <LoadingState label="Checking the Mail Server connection…" />;
  const text =
    state.kind === 'not_connected'
      ? 'Mailbox management isn’t connected to the Mail Server yet, so mailboxes can’t be changed here.'
      : state.kind === 'unreachable'
        ? 'The Mail Server can’t be reached right now, so mailboxes can’t be changed. Nothing has been changed.'
        : 'Mailboxes couldn’t be loaded, so no change can be made right now. Nothing has been changed.';
  return (
    <section
      aria-label="Mail Server not available"
      className="rounded-[var(--radius-card)] border border-line bg-surface p-5 text-sm"
    >
      <p className="text-fg-2">{text}</p>
      <p className="mt-3">
        <Link
          to="/app/mailboxes"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Back to Mailboxes
        </Link>
      </p>
    </section>
  );
}
